import { Link } from "react-router-dom";
import { ArrowUpRight, ShoppingBag, Cpu, MessageCircle } from "lucide-react";
import { PageHeader, Loader } from "@/components/common/Loader";
import { useFetch } from "@/lib/api";
import { SpendingCard } from "@/components/common/Spending";

const Initials = ({ title }) => (
  <div className="w-full h-full flex items-center justify-center bg-ivory-deep">
    <span className="font-serif text-3xl text-gold/80">{title.replace(/[^A-Za-zÀ-ÿ]/g, "").slice(0, 2)}</span>
  </div>
);

function NeverTried({ products }) {
  if (!products.length) return null;
  return (
    <section className="mt-14" data-testid="never-tried-section">
      <p className="eyebrow mb-2">Pensati per te</p>
      <h2 className="text-2xl sm:text-3xl font-light mb-6">Non hai mai provato</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {products.map((p) => (
          <article key={p.id} data-testid={`never-tried-${p.id}`} className="lux-card flex gap-4 p-4 sm:p-5">
            <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 overflow-hidden bg-white">
              {p.image ? <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" /> : <Initials title={p.title} />}
            </div>
            <div className="flex flex-col min-w-0">
              <p className="font-serif text-xl leading-snug">{p.title}</p>
              {p.description && <p className="text-sm text-warm-text mt-2 leading-relaxed line-clamp-3">{p.description}</p>}
              <a href={p.url} target="_blank" rel="noreferrer" data-testid={`never-tried-buy-${p.id}`} className="btn-link inline-flex items-center gap-1 mt-auto pt-3">
                Acquistalo ora <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function MachinesBanner() {
  const { data } = useFetch("/shop/machines");
  if (!data?.length) return null;
  return (
    <section data-testid="machines-banner" className="mt-14 relative overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/[0.12] via-noir to-noir p-6 sm:p-10 animate-fade-up shadow-[0_30px_80px_-30px_rgba(212,175,55,0.35)]">
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/15 blur-3xl animate-float" />
      <div className="relative flex items-center gap-3"><Cpu className="w-5 h-5 text-gold" strokeWidth={1.4} /><p className="eyebrow">Tecnologia Skin Lovers</p></div>
      <h2 className="relative text-3xl sm:text-4xl font-light mt-3">I nostri <em className="text-gold-gradient">macchinari</em></h2>
      <p className="relative text-sm text-warm-text mt-2 max-w-lg">Apparecchiature professionali per potenziare i trattamenti del tuo centro.</p>
      <div className="relative mt-8 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 lg:grid lg:grid-cols-4 lg:overflow-visible lg:mx-0 lg:px-0">
        {data.map((m) => (
          <div key={m.id} data-testid={`machine-${m.id}`}
            className="group snap-start shrink-0 w-48 sm:w-56 lg:w-auto rounded-2xl overflow-hidden border border-white/[0.08] bg-ivory-card hover:border-gold/60 transition-[border-color,transform] duration-300 hover:-translate-y-1 flex flex-col">
            <a href={m.url} target="_blank" rel="noreferrer" data-testid={`machine-shop-${m.id}`} className="block">
              <div className="aspect-square bg-white overflow-hidden">
                {m.image ? <img src={m.image} alt={m.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <Initials title={m.title} />}
              </div>
              <div className="p-4 pb-2">
                <p className="font-serif text-lg leading-snug line-clamp-2">{m.title}</p>
                <span className="btn-link inline-flex items-center gap-1 mt-2 text-xs">Scopri nello shop <ArrowUpRight className="w-3.5 h-3.5" /></span>
              </div>
            </a>
            <Link to={`/assistenza/nuovo?macchinario=${encodeURIComponent(m.title)}`} data-testid={`machine-info-${m.id}`}
              className="mt-auto mx-4 mb-4 inline-flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-full border border-gold/50 text-[10px] font-semibold uppercase tracking-[0.08em] text-white whitespace-nowrap hover:bg-gold/15 hover:text-gold-light transition-colors">
              <MessageCircle className="w-3.5 h-3.5 text-gold" /> Chiedi informazioni
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Orders() {
  const { data, loading } = useFetch("/shop/suggestions");
  return (
    <div>
      <PageHeader eyebrow="Ordini" title="Ordina per il tuo centro" subtitle="Gli ordini si fanno sul negozio riservato Skin Lovers, con il tuo solito accesso." />
      <div className="mb-10"><SpendingCard endpoint="/shop/spending" /></div>
      {loading || !data ? <Loader /> : (
        <>
          <a href={data.shop_url} target="_blank" rel="noreferrer" data-testid="order-shop-btn" className="btn-gold w-full sm:w-auto h-14 px-10 text-[13px]">
            <ShoppingBag className="w-4 h-4" /> Ordina
          </a>
          <NeverTried products={data.products} />
          <MachinesBanner />
        </>
      )}
    </div>
  );
}
